import { WorkloadType } from '@/components/Workload/types';
import { GROUPS, PREFIX_PROXY } from '@/service/apiConf';
import { deleteRes, get, patchJSON, patchJsonPatch, postJSON, putJSON } from '@/service/request';
import { TimeStep } from '@/types';

// 获取工作负载的所有项目
export const getWorkloadNamespaces = (cluster: string, params?: any): Promise<any> =>
  get(`${PREFIX_PROXY.vapis(GROUPS.namespace)}/namespaces`, params);

// 获取工作负载的列表
export interface GetWorkloadListParams {
  name?: string;
  page?: number;
  limit?: number;
  ascending?: boolean;
  sortBy?: string;
  labelSelector?: string;
  creator?: string;
  namespace?: string;
  workspace?: string;
  status?: string; // 小写
}
/** 集群的接口都需要带 cluster  */
export const getDeploymentList = (cluster: string, params: GetWorkloadListParams): Promise<any> =>
  get(`${PREFIX_PROXY.vapis(GROUPS.deployment)}/deployments`, params);

// 获取工作负载的详情
export const getDeploymentDetail = ({
  cluster,
  namespace,
  deployment,
}: {
  cluster: string;
  namespace: string;
  deployment: string;
}): Promise<any> =>
  get(`${PREFIX_PROXY.apis_v1(GROUPS.apps)}/namespaces/${namespace}/deployments/${deployment}`);

/**
 * 创建工作负载
 * 创建之前需要挟带 dryRun=All 参数，用于检查是否可以创建
 * 如果返回 201, 那么可以创建, 去掉 dryRun=All 参数再次请求
 */
export const postCreateDeployment = ({
  cluster,
  namespace,
  data,
  dryRun,
}: {
  cluster: string;
  namespace: string;
  data: any;
  dryRun?: string;
}): Promise<any> =>
  postJSON(`${PREFIX_PROXY.apis_v1(GROUPS.apps)}/namespaces/${namespace}/deployments`, data, {
    params: { dryRun },
  } as any);

/** 编辑工作负载 */
export const putEditDeployment = ({
  cluster,
  namespace,
  deployment,
  data,
}: {
  cluster: string;
  namespace: string;
  deployment: string;
  data: any;
}): Promise<any> =>
  putJSON(
    `${PREFIX_PROXY.apis_v1(GROUPS.apps)}/namespaces/${namespace}/deployments/${deployment}`,
    data,
  );

/** 默认是: application/merge-patch+json */
export const patchEditDeployment = ({
  cluster,
  namespace,
  deployment,
  data,
}: {
  cluster: string;
  namespace: string;
  deployment: string;
  data: any;
}): Promise<any> =>
  patchJSON(
    `${PREFIX_PROXY.apis_v1(GROUPS.apps)}/namespaces/${namespace}/deployments/${deployment}`,
    data,
  );

/** application/json-patch+json */
export const patchEditDeployment2 = ({
  cluster,
  namespace,
  deployment,
  data,
}: {
  cluster: string;
  namespace: string;
  deployment: string;
  data: any;
}): Promise<any> =>
  patchJsonPatch(
    `${PREFIX_PROXY.apis_v1(GROUPS.apps)}/namespaces/${namespace}/deployments/${deployment}`,
    data,
  );

/** 删除工作负载 */
export const deleteDeployment = ({
  cluster,
  namespace,
  deployment,
}: {
  cluster: string;
  namespace: string;
  deployment: string;
}): Promise<any> =>
  deleteRes(
    `${PREFIX_PROXY.apis_v1(GROUPS.apps)}/namespaces/${namespace}/deployments/${deployment}`,
  );

export interface GetWorkloadMonitorParams {
  /** 筛选资源: pod_cpu_usage pod_memory_usage_wo_cache pod_net_bytes_transmitted pod_net_bytes_received */
  metrics_filter?: string;
  start?: number;
  end?: number;
  step?: TimeStep;
  times?: number;
}

/** 获取工作负载的监控详情 */
export const getWorkloadMonitor = ({
  module, // 工作负载类型: deployment, statefulset, daemonset 见 WorkloadType 枚举
  cluster,
  namespace,
  deployment,
  params,
}: {
  module: WorkloadType;
  cluster: string;
  namespace: string;
  deployment: string;
  params?: GetWorkloadMonitorParams;
}): Promise<any> =>
  get(
    `${PREFIX_PROXY.vapis(GROUPS.monitoring)}/namespaces/${namespace}/workloads/${module}/${deployment}/pods`,
    params,
  );

/** 自动扩缩容 */
export const postAutoScale = ({
  cluster,
  namespace,
  data,
}: {
  cluster: string;
  namespace: string;
  data: any;
}): Promise<any> =>
  postJSON(
    `${PREFIX_PROXY.apis_v2(GROUPS.autoscaling)}/namespaces/${namespace}/horizontalpodautoscalers?fieldManager=kubectl-autoscale`,
    data,
  );

export const getAutoScale = ({
  cluster,
  namespace,
  deployment,
}: {
  cluster: string;
  namespace: string;
  deployment: string;
}): Promise<any> =>
  get(
    `${PREFIX_PROXY.apis_v2(GROUPS.autoscaling)}/namespaces/${namespace}/horizontalpodautoscalers/${deployment}`,
  );

export const patchAutoScale = ({
  cluster,
  namespace,
  deployment,
  data,
}: {
  cluster: string;
  namespace: string;
  deployment: string;
  data: any;
}): Promise<any> =>
  patchJSON(
    `${PREFIX_PROXY.apis_v2(GROUPS.autoscaling)}/namespaces/${namespace}/horizontalpodautoscalers/${deployment}`,
    data,
  );

export const deleteAutoScale = ({
  cluster,
  namespace,
  deployment,
}: {
  cluster: string;
  namespace: string;
  deployment: string;
}): Promise<any> =>
  deleteRes(
    `${PREFIX_PROXY.apis_v2(GROUPS.autoscaling)}/namespaces/${namespace}/horizontalpodautoscalers/${deployment}`,
  );
